import type { LucideIcon } from 'lucide-react'
import { ArrowRight } from 'lucide-react'
import { getQuoteOrderValue, type QuoteListRow } from 'src/api/db/apiQuote'
import { QuoteOrderValue } from 'src/shared/components/QuoteOrderValue'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import { Image } from 'src/shared/components/ui/image'
import { cn } from 'src/shared/components/ui/utils'
import { getQuotePreviewThumbnails } from 'src/shared/utils/quoteItemThumbnail'

export type ActionTone = 'violet' | 'orange' | 'amber' | 'teal' | 'blue'

const TONE = {
  violet: {
    icon: 'bg-violet-100 text-violet-700 group-hover:bg-violet-200/90',
    wash: 'from-violet-500/10',
    badge: 'purple' as const,
  },
  orange: {
    icon: 'bg-orange-100 text-orange-700 group-hover:bg-orange-200/90',
    wash: 'from-orange-500/12',
    badge: 'orange' as const,
  },
  amber: {
    icon: 'bg-amber-100 text-amber-800 group-hover:bg-amber-200/90',
    wash: 'from-amber-500/12',
    badge: 'yellow' as const,
  },
  teal: {
    icon: 'bg-teal-100 text-teal-700 group-hover:bg-teal-200/90',
    wash: 'from-teal-500/10',
    badge: 'green' as const,
  },
  blue: {
    icon: 'bg-sky-100 text-sky-700 group-hover:bg-sky-200/90',
    wash: 'from-sky-500/10',
    badge: 'blue' as const,
  },
}

export const ActionCard = ({
  icon: Icon,
  title,
  subtitle,
  badge,
  tone = 'orange',
  quote,
  onClick,
  className,
}: {
  icon: LucideIcon
  title: string
  subtitle?: string
  badge: string
  tone?: ActionTone
  quote?: QuoteListRow
  onClick: () => void
  className?: string
}) => {
  const style = TONE[tone]
  const { previews } = getQuotePreviewThumbnails(quote?.quote_item, 3)
  const visible = previews.filter((p) => p.url)
  const orderValue = quote ? getQuoteOrderValue(quote) : 0

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'card-new group relative w-full overflow-hidden p-3.5 text-left',
        'transition-transform duration-200 hover:-translate-y-1',
        className
      )}
    >
      <div
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-br via-transparent to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100',
          style.wash
        )}
      />
      <Row className="relative items-start gap-3">
        {visible.length > 0 ? (
          <div className="relative shrink-0 pt-0.5">
            <Row className="items-center">
              {visible.map(({ key, url }, index) => (
                <div
                  key={key}
                  className={cn(
                    'h-11 w-11 shrink-0 overflow-hidden rounded-xl border border-white bg-muted shadow-soft-sm',
                    index > 0 && '-ml-2.5'
                  )}
                >
                  <Image
                    src={url!}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </Row>
            <div
              className={cn(
                'absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-md border border-white shadow-soft-sm',
                style.icon
              )}
            >
              <Icon className="h-3 w-3" strokeWidth={2.5} />
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors',
              style.icon
            )}
          >
            <Icon className="h-5 w-5" strokeWidth={2} />
          </div>
        )}
        <Column className="min-w-0 flex-1 gap-2">
          <Row className="items-start justify-between gap-2">
            <Column className="min-w-0 gap-0.5">
              <p className="truncate text-sm font-medium leading-snug">
                {title}
              </p>
              {subtitle ? (
                <p className="truncate text-xs text-muted-foreground">
                  {subtitle}
                </p>
              ) : null}
            </Column>
            <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
          </Row>
          <Row className="items-center justify-between gap-2">
            <Badge variant={style.badge} size="sm" className="w-fit">
              {badge}
            </Badge>
            <QuoteOrderValue amount={orderValue} size="sm" />
          </Row>
        </Column>
      </Row>
    </button>
  )
}
